import React from 'react';
import { FloatLabel } from "primereact/floatlabel";
import { InputText as InputTextP } from "primereact/inputtext";
import { InputTextarea as InputTextareaP } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { Password } from "primereact/password";
import { InputMask } from "primereact/inputmask";
import { useTranslation } from 'react-i18next';

const InputText = ({
    id,
    name,
    type = "text",
    value,
    onChange,
    onBlur,
    label,
    clearFunction,
    error,
    touched,
    required = false,
    showTemplate = true,
    placeholder = "",
    className = "",
    rows = 3,
    showFooter,
    min,
    max,
    step,
    minFractionDigits = 0,
    maxFractionDigits = 2,
    currency,
    locale = "tr-TR",
    camelCase = false,
    ...props
}) => {
    const { t } = useTranslation();

    const formatCamelCase = (text) => {
        if (!camelCase || !text) return text;

        let result = '';
        let capitalizeNext = true;

        for (let i = 0; i < text.length; i++) {
            const char = text[i];

            if (char === '.' || char === '/') {
                result += char;
                const nextChar = text[i + 1];
                if (nextChar && /^[a-zA-Z]$/.test(nextChar)) {
                    result += ' ';
                    capitalizeNext = true;
                } else {
                    capitalizeNext = false;
                }
            }
            else if (char === ' ') {
                result += char;
                capitalizeNext = true;
            }
            else {
                if (capitalizeNext) {
                    result += char.toUpperCase();
                    capitalizeNext = false;
                } else {
                    result += char.toLowerCase();
                }
            }
        }

        return result;
    };



    const handleCamelCaseChange = (e) => {
        if (camelCase && (type === 'text' || type === 'textarea' || type === 'area')) {
            const formattedValue = formatCamelCase(e.target.value);
            const syntheticEvent = {
                target: {
                    name: e.target.name,
                    value: formattedValue
                }
            };
            onChange(syntheticEvent);
        } else {
            onChange(e);
        }
    };

    const passwordFooter = (
        <>
            <p className="mt-2 text-xs">{t('passwordRequirements')}:</p>
            <ul className="pl-2 ml-2 mt-0 text-xs">
                <li>*{t('passwordLowercase')}</li>
                <li>*{t('passwordUppercase')}</li>
                <li>*{t('passwordNumber')}</li>
                <li>*{t('passwordSpecial')}</li>
                <li>*{t('passwordMinLength')}</li>
            </ul>
        </>
    );

    const getInputComponent = () => {
        const baseClassName = `w-full shadow-xl rounded-lg border-2 p-2 ${touched && error ? 'p-invalid' : ''} ${className}`;

        const inputOnChange = (camelCase && (type === 'text' || type === 'textarea' || type === 'area')) ? handleCamelCaseChange : onChange;

        switch (type) {
            case 'number':
                return (
                    <InputNumber
                        id={id}
                        name={name}
                        value={value || null}
                        onValueChange={(e) => {
                            onChange({
                                target: {
                                    name,
                                    value: e.value
                                }
                            });
                        }}
                        onBlur={onBlur}
                        className={`w-full ${value !== null && value !== undefined && value !== '' ? 'p-inputnumber-filled' : ''}`}
                        inputClassName={baseClassName}
                        placeholder={placeholder}
                        min={min}
                        max={max}
                        step={step}
                        minFractionDigits={minFractionDigits}
                        maxFractionDigits={maxFractionDigits}
                        locale={locale}
                        currency={currency}
                        mode={currency ? "currency" : "decimal"}
                        {...props}
                    />
                );

            case 'password':
                return (
                    <Password
                        id={id}
                        name={name}
                        value={value || ""}
                        onChange={onChange}
                        onBlur={onBlur}
                        toggleMask
                        footer={showTemplate ? passwordFooter : false}
                        className={`w-full ${value && value.length > 0 ? 'p-password-filled' : ''}`}
                        inputClassName={baseClassName}
                        placeholder={placeholder}
                        feedback={showTemplate && (props.feedback !== false)}
                        {...props}
                    />
                );

            case 'phone':
                return (
                    <InputMask
                        id={id}
                        name={name}
                        mask="0 (5*9) 999-99-99"
                        value={value || ""}
                        onChange={onChange}
                        onBlur={onBlur}
                        placeholder="0 (5__) ___-__-__"
                        className={baseClassName}
                        {...props}
                    />
                );

            case 'date':
            case 'calendar':
                return (
                    <InputTextP
                        id={id}
                        name={name}
                        placeholder='Tarih Seçiniz'
                        type="date"
                        value={value instanceof Date && !isNaN(value.getTime())
                            ? value.toISOString().split('T')[0]
                            : (typeof value === 'string' ? value : "")}
                        onChange={(e) => {
                            const inputValue = e.target.value;
                            if (inputValue && inputValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                const dateObj = new Date(inputValue + 'T00:00:00.000Z');
                                if (!isNaN(dateObj.getTime())) {
                                    onChange({ target: { name, value: dateObj } });
                                    return;
                                }
                            }
                            onChange({ target: { name, value: inputValue || null } });
                        }}
                        onBlur={onBlur}
                        className={baseClassName}
                        {...props}
                    />
                );

            case 'area':
            case 'textarea':
                return (
                    <InputTextareaP
                        id={id}
                        name={name}
                        value={value || ""}
                        onChange={inputOnChange}
                        onBlur={onBlur}
                        className={baseClassName}
                        placeholder={placeholder}
                        rows={rows}
                        {...props}
                    />
                );

            default:
                return (
                    <InputTextP
                        id={id}
                        name={name}
                        type={type}
                        value={value || ""}
                        onChange={inputOnChange}
                        onBlur={onBlur}
                        className={baseClassName}
                        placeholder={placeholder}
                        {...props}
                    />
                );
        }
    };

    const getDisplayValue = () => {
        if ((type === 'date' || type === 'calendar') && value) {
            if (value instanceof Date && !isNaN(value.getTime())) {
                return value.toISOString().split('T')[0];
            }
            if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
                return value;
            }
            return "";
        }
        if (type === 'number') {
            return value !== null && value !== undefined && value !== '' ? value : null;
        }
        return value || "";
    };

    const displayValue = (type === 'date' || type === 'calendar' || type === 'number') ? getDisplayValue() : (value || "");

    const getClearIconPosition = () => {
        if (type === 'area' || type === 'textarea') {
            return "right-3 top-3";
        }
        if (type === 'date' || type === 'calendar') {
            return "right-10 top-1/2 -translate-y-1/2";
        }
        return "right-3 top-1/2 -translate-y-1/2";
    };

    const handleClear = () => {
        if (clearFunction) {
            clearFunction();
        } else {
            if (type === 'number') {
                onChange({ target: { name, value: null } });
            } else {
                onChange({ target: { name, value: '' } });
            }
        }
    };

    return (
        <>
            <style jsx>{`
                /* FloatLabel Container - Kompakt yapı */
                .p-float-label {
                    width: 100%;
                    position: relative;
                    margin: 0 !important;
                    padding: 0 !important;
                }

                /* Base Input Styles - Kompakt padding */
                .p-inputtext,
                .p-inputmask,
                .p-inputtextarea,
                .p-password-input,
                .p-inputnumber-input {
                    width: 100% !important;
                    padding: 0.75rem 0.75rem !important;
                    font-size: 1rem !important;
                    line-height: 1.25 !important;
                    border-radius: 0.5rem !important;
                    border: 2px solid rgb(220, 220, 220) !important;
                    background-color: white !important;
                    color: black !important;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
                    margin: 0 !important;
                    resize: vertical !important;
                }

                /* InputNumber Container */
                .p-inputnumber {
                    width: 100% !important;
                    display: block !important;
                    margin: 0 !important;
                }

                .p-inputnumber .p-component {
                    width: 100% !important;
                    margin: 0 !important;
                }

                /* InputNumber Buttons */
                .p-inputnumber-buttons-stacked .p-inputnumber-button {
                    background: rgb(249, 250, 251) !important;
                    border: 1px solid rgb(220, 220, 220) !important;
                    color: rgb(107, 114, 128) !important;
                    width: 2rem !important;
                    transition: all 0.2s ease !important;
                }

                .p-inputnumber-buttons-stacked .p-inputnumber-button:hover {
                    background: rgb(243, 244, 246) !important;
                    border-color: rgb(107, 114, 128) !important;
                    color: rgb(75, 85, 99) !important;
                }

                .p-inputnumber-buttons-stacked .p-inputnumber-button-up {
                    border-radius: 0 0.5rem 0 0 !important;
                    border-bottom-width: 0 !important;
                }

                .p-inputnumber-buttons-stacked .p-inputnumber-button-down {
                    border-radius: 0 0 0.5rem 0 !important;
                }

                /* Label Styles - Kompakt pozisyonlama */
                .p-float-label label {
                    position: absolute !important;
                    left: 0.75rem !important;
                    top: 50% !important;
                    transform: translateY(-50%) !important;
                    color: rgb(75, 85, 99) !important;
                    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1) !important;
                    font-weight: 500 !important;
                    font-size: 1rem !important;
                    pointer-events: none !important;
                    background: transparent !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    z-index: 1 !important;
                }

                /* Textarea Label Positioning */
                .p-float-label .p-inputtextarea ~ label {
                    top: 1rem !important;
                    transform: translateY(0) !important;
                }

                /* Standard Input Active/Focused Label */
                .p-float-label input:focus ~ label,
                .p-float-label input.p-filled ~ label,
                .p-float-label textarea:focus ~ label,
                .p-float-label textarea.p-filled ~ label,
                .p-float-label input[placeholder]:not([placeholder=""]) ~ label {
                    top: -0.9rem !important;
                    left: 0.5rem !important;
                    transform: translateY(0) !important;
                    font-size: 0.75rem !important;
                    background: transparent !important;
                    padding: 0 !important;
                    border-radius: 0 !important;
                    color: rgb(107, 114, 128) !important;
                    font-weight: 600 !important;
                    z-index: 2 !important;
                }

                /* InputNumber Specific Label States */
                .p-float-label .p-inputnumber:focus-within ~ label,
                .p-float-label .p-inputnumber-filled ~ label {
                    top: -0.9rem !important;
                    left: 0.5rem !important;
                    transform: translateY(0) !important;
                    font-size: 0.75rem !important;
                    background: transparent !important;
                    padding: 0 !important;
                    border-radius: 0 !important;
                    color: rgb(107, 114, 128) !important;
                    font-weight: 600 !important;
                    z-index: 2 !important;
                }

                /* Password Component Specific Selectors */
                .p-float-label .p-password:focus-within ~ label,
                .p-float-label .p-password-filled ~ label,
                .p-float-label .p-password.p-filled ~ label,
                .p-float-label .p-password-input:focus ~ label,
                .p-float-label .p-password-input.p-filled ~ label {
                    top: -0.9rem !important;
                    left: 0.5rem !important;
                    transform: translateY(0) !important;
                    font-size: 0.75rem !important;
                    background: transparent !important;
                    padding: 0 !important;
                    border-radius: 0 !important;
                    color: rgb(107, 114, 128) !important;
                    font-weight: 600 !important;
                    z-index: 2 !important;
                }

                /* Focus States */
                .p-inputtext:focus,
                .p-inputmask:focus,
                .p-inputtextarea:focus,
                .p-password-input:focus,
                .p-inputnumber-input:focus {
                    border-color: rgb(107, 114, 128) !important;
                    box-shadow: 0 0 0 1px rgb(107, 114, 128), 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
                    outline: none !important;
                }

                /* Password Container */
                .p-password {
                    width: 100% !important;
                    display: block !important;
                    margin: 0 !important;
                }

                .p-password .p-component {
                    width: 100% !important;
                    margin: 0 !important;
                }

                /* Icons */
                .p-password .p-password-toggle-icon {
                    color: rgb(107, 114, 128) !important;
                    cursor: pointer !important;
                    transition: color 0.2s ease !important;
                    font-size: 1rem !important;
                    right: 0.75rem !important;
                    top: 50% !important;
                    transform: translateY(-50%) !important;
                }

                .p-password .p-password-toggle-icon:hover {
                    color: rgb(75, 85, 99) !important;
                }

                /* Dark Mode Styles */
                :root.dark .p-inputtext,
                :root.dark .p-inputmask,
                :root.dark .p-inputtextarea,
                :root.dark .p-password-input,
                :root.dark .p-inputnumber-input {
                    background-color: rgb(31, 41, 55) !important;
                    border-color: rgb(75, 85, 99) !important;
                    color: white !important;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.12) !important;
                }

                :root.dark .p-inputnumber-buttons-stacked .p-inputnumber-button {
                    background: rgb(55, 65, 81) !important;
                    border-color: rgb(75, 85, 99) !important;
                    color: rgb(156, 163, 175) !important;
                }

                :root.dark .p-inputnumber-buttons-stacked .p-inputnumber-button:hover {
                    background: rgb(75, 85, 99) !important;
                    color: rgb(209, 213, 219) !important;
                }

                :root.dark .p-inputtext:focus,
                :root.dark .p-inputmask:focus,
                :root.dark .p-inputtextarea:focus,
                :root.dark .p-password-input:focus,
                :root.dark .p-inputnumber-input:focus {
                    border-color: rgb(156, 163, 175) !important;
                    box-shadow: 0 0 0 1px rgb(156, 163, 175), 0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.12) !important;
                }

                :root.dark .p-float-label label {
                    color: rgb(156, 163, 175) !important;
                }

                :root.dark .p-float-label input:focus ~ label,
                :root.dark .p-float-label input.p-filled ~ label,
                :root.dark .p-float-label textarea:focus ~ label,
                :root.dark .p-float-label textarea.p-filled ~ label,
                :root.dark .p-float-label input[placeholder]:not([placeholder=""]) ~ label,
                :root.dark .p-float-label .p-password:focus-within ~ label,
                :root.dark .p-float-label .p-password.p-filled ~ label,
                :root.dark .p-float-label .p-password-filled ~ label,
                :root.dark .p-float-label .p-password-input:focus ~ label,
                :root.dark .p-float-label .p-password-input.p-filled ~ label,
                :root.dark .p-float-label .p-inputnumber:focus-within ~ label,
                :root.dark .p-float-label .p-inputnumber-filled ~ label {
                    background: transparent !important;
                    color: rgb(156, 163, 175) !important;
                    padding: 0 !important;
                    border-radius: 0 !important;
                }

                /* Clear Icon */
                .pi-times {
                    color: rgb(107, 114, 128) !important;
                    transition: all 0.2s ease !important;
                    font-size: 14px !important;
                    z-index: 10 !important;
                    cursor: pointer !important;
                }

                .pi-times:hover {
                    color: rgb(75, 85, 99) !important;
                }

                :root.dark .pi-times {
                    color: rgb(156, 163, 175) !important;
                }

                :root.dark .pi-times:hover {
                    color: rgb(209, 213, 219) !important;
                }

                /* Error States */
                .p-invalid {
                    border-color: #dc3545 !important;
                    box-shadow: 0 0 0 1px #dc3545, 0 4px 6px -1px rgba(220, 53, 69, 0.1) !important;
                }

                .p-password.p-invalid .p-password-input,
                .p-inputnumber.p-invalid .p-inputnumber-input {
                    border-color: #dc3545 !important;
                    box-shadow: 0 0 0 1px #dc3545, 0 4px 6px -1px rgba(220, 53, 69, 0.1) !important;
                }

                .p-error {
                    color: #dc3545 !important;
                    font-size: 9px !important;
                    font-weight: 500 !important;
                    margin-top: 0.25rem !important;
                }

                /* Responsive */
                @media screen and (max-width: 768px) {
                    .p-inputtext,
                    .p-inputmask,
                    .p-inputtextarea,
                    .p-password-input,
                    .p-inputnumber-input {
                        font-size: 16px !important;
                        padding: 0.75rem !important;
                    }

                    .p-float-label label {
                        font-size: 16px !important;
                    }

                    .p-float-label input:focus ~ label,
                    .p-float-label input.p-filled ~ label,
                    .p-float-label textarea:focus ~ label,
                    .p-float-label textarea.p-filled ~ label,
                    .p-float-label input[placeholder]:not([placeholder=""]) ~ label,
                    .p-float-label .p-password:focus-within ~ label,
                    .p-float-label .p-password.p-filled ~ label,
                    .p-float-label .p-password-filled ~ label,
                    .p-float-label .p-inputnumber:focus-within ~ label,
                    .p-float-label .p-inputnumber-filled ~ label {
                        font-size: 12px !important;
                        top: -0.9rem !important;
                    }
                }
            `}</style>

            <div className="relative w-full">
                <FloatLabel>
                    {React.cloneElement(getInputComponent(), {
                        value: displayValue
                    })}
                    <label htmlFor={id} className="ml-2.5">
                        {label} {required && <span className="text-red-500">*</span>}
                    </label>
                    {((displayValue !== null && displayValue !== undefined && displayValue !== '') && type !== 'password') && (
                        <i
                            className={`pi pi-times cursor-pointer absolute ${getClearIconPosition()}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClear();
                            }}
                        />
                    )}
                </FloatLabel>
                {touched && error && <small className="p-error block text-end">{error}</small>}
            </div>
        </>
    );
};

export default InputText;